import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getChatApiRoute } from '../api/v1Client'
import { useAuth } from '../providers'
import type { components } from '../types/backend-openapi'

export type Person = components['schemas']['Person']

export const PEOPLE_QUERY_KEY = 'people'

const PAGE_LIMIT = 100

interface PeopleListResponse {
  success: true
  data: Array<Person>
  nextCursor?: string
}

async function fetchAllPeople(token: string): Promise<Array<Person>> {
  const collected: Array<Person> = []
  let cursor: string | undefined

  do {
    const url = new URL(getChatApiRoute('api/v1/people'))
    url.searchParams.set('limit', String(PAGE_LIMIT))
    if (cursor) url.searchParams.set('cursor', cursor)

    const response = await fetch(url.toString(), {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      const body = await response.text().catch(() => '')
      throw new Error(`GET /people failed: ${response.status} ${body}`)
    }

    const json = (await response.json()) as PeopleListResponse
    if (json.data?.length) collected.push(...json.data)
    cursor = json.nextCursor
  } while (cursor)

  return collected
}

export const usePeople = () => {
  const { getAuthToken } = useAuth()

  return useQuery({
    queryKey: [PEOPLE_QUERY_KEY],
    queryFn: async (): Promise<Array<Person>> => {
      const token = await getAuthToken()
      return fetchAllPeople(token)
    },
    staleTime: 1000 * 60 * 10,
  })
}

/**
 * Map of lowercased email → Person, indexed across primaryEmail, additionalEmails, and aliases.
 * Used to resolve calendar attendee emails (including alias addresses) to a single canonical Person.
 */
export const usePeopleByEmail = () => {
  const { data: people } = usePeople()

  return useMemo(() => {
    const byEmail = new Map<string, Person>()
    if (!people) return byEmail

    const addEmail = (email: string | undefined, person: Person) => {
      if (!email) return
      const key = email.trim().toLowerCase()
      if (!key) return
      // First match wins — primaryEmail is added first, so it takes precedence over aliases.
      if (!byEmail.has(key)) byEmail.set(key, person)
    }

    for (const person of people) {
      addEmail(person.primaryEmail, person)
      for (const email of person.additionalEmails ?? []) addEmail(email, person)
      for (const alias of person.aliases ?? []) addEmail(alias, person)
    }
    return byEmail
  }, [people])
}
